import React, { useState } from 'react';
import CTA from '../components/CTA';
import { generateCTAMessage } from '../utils/ctaMessages.js';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: ''
      });
    }, 2000);
  };

  const contactMethods = [
    {
      icon: 'bi-geo-alt',
      title: 'Visit Us',
      details: 'Austin Texas, United States'
    },
    {
      icon: 'bi-envelope',
      title: 'Email Us',
      details: 'contact@vallenai.com'
    }
  ];

  const services = [
    'Generative AI',
    'Machine Learning',
    'Natural Language Processing',
    'Chatbot Development',
    'SaaS Development',
    'Predictive Analytics',
    'Custom Software',
    'UI/UX Design',
    'Staff Augmentation',
    'Other'
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="contact-hero-section">
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        {/* Floating Particles */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: 'rgba(56, 189, 248, 0.4)',
              borderRadius: '50%',
              top: `${15 + i * 12}%`,
              left: `${8 + i * 15}%`,
              animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`
            }}></div>
          ))}
        </div>

        <div className="container" style={{ position: 'relative', zIndex: 3 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              
              {/* Main Title */}
              <h1 className="contact-hero-title">
                Ready to Transform Your Business with{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>AI Solutions?</span>
              </h1>
              
              {/* Subtitle */}
              <p className="contact-hero-subtitle">
                Get in touch with our AI experts to discuss your project, explore possibilities, and discover how we can help you achieve your business goals with cutting-edge technology.
              </p>
              
              {/* Quick Stats */}
              <div className="contact-stats-grid">
                <div className="row g-4 justify-content-center">
                  <div className="col-6 col-md-3">
                    <div className="contact-stat-card" style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(14, 165, 233, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <div className="contact-stat-icon">
                        <i className="bi bi-clock"></i>
                      </div>
                      <div className="contact-stat-number">24h</div>
                      <div className="contact-stat-label">Response Time</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="contact-stat-card" style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(14, 165, 233, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <div className="contact-stat-icon">
                        <i className="bi bi-people-fill"></i>
                      </div>
                      <div className="contact-stat-number">Free</div>
                      <div className="contact-stat-label">Consultation</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="contact-stat-card" style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(14, 165, 233, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <div className="contact-stat-icon">
                        <i className="bi bi-shield-check"></i>
                      </div>
                      <div className="contact-stat-number">100%</div>
                      <div className="contact-stat-label">Confidential</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="contact-stat-card" style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(14, 165, 233, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <div className="contact-stat-icon">
                        <i className="bi bi-globe"></i>
                      </div>
                      <div className="contact-stat-number">Global</div>
                      <div className="contact-stat-label">Support</div>
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          .contact-hero-badge {
            animation: pulse 3s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Contact Methods */}
      <section className="py-5" style={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: '6rem 0'
      }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: '900',
              color: '#1e293b',
              marginBottom: '1rem'
            }}>Get in Touch</h2>
            <p style={{
              fontSize: '1.3rem',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.7',
              fontWeight: '500'
            }}>
              Choose the best way to reach us based on your needs and let's start your AI transformation journey
            </p>
          </div>
          <div className="row g-4 justify-content-center">
            {contactMethods.map((method, index) => (
              <div key={index} className="col-lg-2 col-md-4 col-sm-6 col-12" style={{ maxWidth: '240px' }}>
                <div style={{
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%), radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.03) 0%, transparent 50%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(14, 165, 233, 0.2)',
                  borderRadius: '28px',
                  padding: '2.5rem 2rem',
                  textAlign: 'center',
                  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  height: '300px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(14, 165, 233, 0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 25px 60px rgba(14, 165, 233, 0.2), 0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                  e.currentTarget.style.background = 'linear-gradient(145deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 0.95) 100%)';
                  const icon = e.currentTarget.querySelector('.contact-icon');
                  if (icon) {
                    icon.style.transform = 'scale(1.15) rotate(5deg)';
                    icon.style.boxShadow = '0 18px 45px rgba(14, 165, 233, 0.4), inset 0 2px 6px rgba(255, 255, 255, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(14, 165, 233, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                  e.currentTarget.style.background = 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)';
                  const icon = e.currentTarget.querySelector('.contact-icon');
                  if (icon) {
                    icon.style.transform = 'scale(1) rotate(0deg)';
                    icon.style.boxShadow = '0 12px 35px rgba(14, 165, 233, 0.25), inset 0 2px 6px rgba(255, 255, 255, 0.25)';
                  }
                }}
                >
                  <div className="contact-icon" style={{
                    width: '92px',
                    height: '92px',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 50%, #0369a1 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.6rem',
                    color: 'white',
                    fontSize: '2.4rem',
                    boxShadow: '0 12px 35px rgba(14, 165, 233, 0.25), inset 0 2px 6px rgba(255, 255, 255, 0.25)',
                    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    position: 'relative'
                  }}>
                    <i className={method.icon}></i>
                  </div>
                  
                  <div style={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <h4 style={{
                      color: '#1e293b',
                      marginBottom: '1.2rem',
                      fontWeight: '700',
                      fontSize: '1.25rem',
                      lineHeight: '1.3',
                      letterSpacing: '-0.02em'
                    }}>{method.title}</h4>
                    <p style={{
                      color: '#0ea5e9',
                      fontWeight: '600',
                      fontSize: '1.05rem',
                      lineHeight: '1.4',
                      margin: 0,
                      wordBreak: 'break-word'
                    }}>{method.details}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-5" style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: '6rem 0'
      }}>
        {/* Enhanced Background Effects */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 15% 85%, rgba(14, 165, 233, 0.12) 0%, transparent 60%),
            radial-gradient(circle at 85% 15%, rgba(14, 165, 233, 0.08) 0%, transparent 60%),
            radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 60%),
            radial-gradient(circle at 25% 25%, rgba(14, 165, 233, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        {/* Subtle Grid Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(14, 165, 233, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          zIndex: 1
        }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="text-center mb-5">
                <h2 style={{
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  fontWeight: '900',
                  color: '#ffffff',
                  textShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                  lineHeight: '1.1',
                  marginBottom: '2rem'
                }}>Tell Us About Your Project</h2>
                <p style={{
                  fontSize: '1.3rem',
                  color: 'rgba(255, 255, 255, 0.95)',
                  lineHeight: '1.7',
                  maxWidth: '600px',
                  margin: '0 auto 3rem',
                  fontWeight: '500'
                }}>
                  Fill out the form below and we'll get back to you within 24 hours with a detailed proposal
                </p>
              </div>

              <div style={{
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: '24px',
                padding: '3rem',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
                position: 'relative',
                overflow: 'hidden'
              }}>

                {submitStatus === 'success' && (
                  <div style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    marginBottom: '2rem',
                    color: '#ffffff',
                    textAlign: 'center'
                  }}>
                    <i className="bi bi-check-circle me-2" style={{ color: '#22c55e' }}></i>
                    Thank you! We've received your message and will get back to you soon.
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        display: 'block'
                      }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1px solid rgba(14, 165, 233, 0.3)',
                          borderRadius: '12px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#1e293b',
                          backdropFilter: 'blur(10px)'
                        }}
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        display: 'block'
                      }}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1px solid rgba(14, 165, 233, 0.3)',
                          borderRadius: '12px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#1e293b',
                          backdropFilter: 'blur(10px)'
                        }}
                        placeholder="your.email@company.com"
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        display: 'block'
                      }}>
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1px solid rgba(14, 165, 233, 0.3)',
                          borderRadius: '12px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#1e293b',
                          backdropFilter: 'blur(10px)'
                        }}
                        placeholder="Your company name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        display: 'block'
                      }}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1px solid rgba(14, 165, 233, 0.3)',
                          borderRadius: '12px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#1e293b',
                          backdropFilter: 'blur(10px)'
                        }}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div className="col-12">
                      <label style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        display: 'block'
                      }}>
                        Service of Interest
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1px solid rgba(14, 165, 233, 0.3)',
                          borderRadius: '12px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#1e293b',
                          backdropFilter: 'blur(10px)'
                        }}
                      >
                        <option value="" style={{ background: '#ffffff', color: '#1e293b' }}>Select a service</option>
                        {services.map((service, index) => (
                          <option key={index} value={service} style={{ background: '#ffffff', color: '#1e293b' }}>{service}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        display: 'block'
                      }}>
                        Project Details *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows="5"
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1px solid rgba(14, 165, 233, 0.3)',
                          borderRadius: '12px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          resize: 'vertical',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#1e293b',
                          backdropFilter: 'blur(10px)'
                        }}
                        placeholder="Tell us about your project, challenges, and goals..."
                      ></textarea>
                    </div>
                    <div className="col-12 text-center">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                          border: 'none',
                          color: '#ffffff',
                          padding: '1rem 3rem',
                          borderRadius: '50px',
                          fontWeight: '600',
                          fontSize: '1rem',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          opacity: isSubmitting ? 0.7 : 1,
                          transition: 'all 0.3s ease',
                          minWidth: '200px',
                          boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubmitting) {
                            e.target.style.transform = 'translateY(-3px)';
                            e.target.style.boxShadow = '0 12px 35px rgba(14, 165, 233, 0.4)';
                            e.target.style.background = 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSubmitting) {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.3)';
                            e.target.style.background = 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)';
                          }
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <i className="bi bi-arrow-clockwise me-2" style={{ animation: 'spin 1s linear infinite' }}></i>
                            Sending...
                          </>
                        ) : (
                          'Send Message'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Austin Map Section */}
      <section className="py-5" style={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: '6rem 0'
      }}>
        <div className="container">
          
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: '24px',
                padding: '2rem',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
                overflow: 'hidden'
              }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d17245.7890123456789!2d-97.7540331!3d30.267321!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f14!3m3!1m2!1s0x8644b599a0cc032f%3A0x5d9b464bd469d57a!2sAustin%2C%20TX%2C%20USA!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%"
                  height="500"
                  style={{
                    border: 0,
                    borderRadius: '16px'
                  }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Austin, Texas Location"
                ></iframe>
                
                <div style={{
                  marginTop: '2rem',
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: 'rgba(14, 165, 233, 0.1)',
                    padding: '1rem 2rem',
                    borderRadius: '16px',
                    border: '1px solid rgba(14, 165, 233, 0.2)'
                  }}>
                    <i className="bi bi-geo-alt" style={{ color: '#0ea5e9', fontSize: '1.2rem' }}></i>
                    <span style={{ color: '#0ea5e9', fontWeight: '600', fontSize: '1rem' }}>
                      Austin, Texas, United States
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA {...generateCTAMessage('page', { type: 'contact' })} />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ContactPage; 